import * as THREE from "three";

/**
 * Camera shake + fullscreen flash + short particle burst.
 */
export class Fx {
  private shakeAmp = 0;
  private shakeT = 0;
  private flashEl: HTMLElement;
  private bursts: { mesh: THREE.Mesh; vel: THREE.Vector3; life: number }[] = [];
  private scene: THREE.Scene | null = null;

  constructor() {
    let el = document.getElementById("fx-flash");
    if (!el) {
      el = document.createElement("div");
      el.id = "fx-flash";
      document.getElementById("app")?.appendChild(el);
    }
    this.flashEl = el;
  }

  attach(scene: THREE.Scene) {
    this.scene = scene;
  }

  shake(amp = 0.35, seconds = 0.22) {
    this.shakeAmp = amp;
    this.shakeT = seconds;
  }

  flash(color = "rgba(0, 229, 255, 0.45)") {
    this.flashEl.style.background = color;
    this.flashEl.classList.remove("on");
    void this.flashEl.offsetWidth;
    this.flashEl.classList.add("on");
  }

  burst(origin: THREE.Vector3, color: number, count = 10) {
    if (!this.scene) return;
    for (let i = 0; i < count; i++) {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 0.12, 0.12),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1 }),
      );
      mesh.position.copy(origin);
      const vel = new THREE.Vector3(
        (Math.random() - 0.5) * 6,
        2 + Math.random() * 4,
        (Math.random() - 0.5) * 6,
      );
      this.scene.add(mesh);
      this.bursts.push({ mesh, vel, life: 0.45 + Math.random() * 0.25 });
    }
  }

  applyShake(camera: THREE.Camera) {
    if (this.shakeT <= 0) return;
    const k = this.shakeT * this.shakeAmp;
    camera.position.x += (Math.random() - 0.5) * k;
    camera.position.y += (Math.random() - 0.5) * k * 0.6;
    camera.position.z += (Math.random() - 0.5) * k;
  }

  update(dt: number) {
    if (this.shakeT > 0) this.shakeT = Math.max(0, this.shakeT - dt);
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      const b = this.bursts[i]!;
      b.life -= dt;
      b.vel.y -= 12 * dt;
      b.mesh.position.addScaledVector(b.vel, dt);
      b.mesh.rotation.x += dt * 8;
      b.mesh.rotation.y += dt * 6;
      const mat = b.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.max(0, b.life * 2);
      if (b.life <= 0) {
        this.scene?.remove(b.mesh);
        b.mesh.geometry.dispose();
        mat.dispose();
        this.bursts.splice(i, 1);
      }
    }
  }

  clear() {
    for (const b of this.bursts) {
      this.scene?.remove(b.mesh);
      b.mesh.geometry.dispose();
      (b.mesh.material as THREE.Material).dispose();
    }
    this.bursts = [];
    this.shakeT = 0;
  }
}
